/**
 * Gemini Live envelope helpers.
 *
 * The runtime forwards Gemini-Live's `serverContent` envelopes verbatim
 * for voice mode (and occasionally for text). The CallClient deliberately
 * does NOT decode them — the SDK stays provider-agnostic. Consumers that
 * need to decode use these helpers.
 *
 * If we ever swap Gemini for OpenAI Realtime or another provider, this
 * file can grow sibling decoders without changing the SDK core.
 */

/**
 * Parse a Gemini-Live `serverContent` envelope into a clean shape.
 * Returns `null` if the message isn't a serverContent envelope.
 *
 * Gemini sends both camelCase and snake_case keys depending on the
 * transport; this helper accepts both.
 *
 * @param {unknown} msg  raw message from CallClient's `raw` event
 * @returns {{
 *   audioParts: string[],   // base64-encoded PCM chunks (in order)
 *   inputText: string|null, // patient transcription this turn
 *   outputText: string|null,// agent transcription this turn
 *   interrupted: boolean,
 *   turnComplete: boolean,
 * } | null}
 */
export function decodeGeminiServerContent(msg) {
  if (!msg || typeof msg !== "object" || msg instanceof ArrayBuffer) return null;
  const sc = msg.serverContent || msg.server_content;
  if (!sc) return null;

  const audioParts = [];
  const turn = sc.modelTurn || sc.model_turn;
  if (turn?.parts) {
    for (const part of turn.parts) {
      const d = part.inlineData || part.inline_data;
      if (d?.data) audioParts.push(d.data);
    }
  }

  return {
    audioParts,
    inputText: (sc.inputTranscription || sc.input_transcription)?.text || null,
    outputText: (sc.outputTranscription || sc.output_transcription)?.text || null,
    interrupted: !!sc.interrupted,
    turnComplete: !!(sc.turnComplete || sc.turn_complete),
  };
}

/**
 * The Gemini-Live `client_content` envelope used to send a user turn.
 * Used by CallClient.sendText() and the voice kickoff path. Exposed so
 * consumers can build their own envelopes if they need to.
 *
 * @param {string} text
 * @returns {object}
 */
export function buildClientContent(text) {
  return {
    client_content: {
      turns: [{ role: "user", parts: [{ text }] }],
      turn_complete: true,
    },
  };
}
